import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { Apollo, gql } from 'apollo-angular';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { DealDispositionsService } from 'src/app/deals/deal-dispositions/deal-dispositions.service';
import {
  DispositionDateInput,
  DispositionInput,
  UpsertDispositionStatus,
} from 'src/app/deals/deal-dispositions/graphql/dispositions';
import Disposition from 'src/app/interfaces/disposition';
import DispositionStatus from 'src/app/interfaces/disposition-status';
import DispositionPipelineDate from 'src/app/interfaces/disposition-pipeline-date';
import Organization from 'src/app/interfaces/organization';
import { DateOutput } from '../disposition-date/disposition-date.component';
import DispositionDate from 'src/app/interfaces/disposition-date';
import { ConfirmationModalComponent } from '../../confirmation-modal/confirmation-modal.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

@Component({
    selector: 'disposition-form',
    templateUrl: './disposition-form.component.html',
    styleUrls: ['./disposition-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DispositionFormComponent {
  @Input() disposition: Disposition;
  @Input() readOnly = true;

  @Output() dispositionChange: EventEmitter<Disposition> =
    new EventEmitter<Disposition>();

  private _dispositionStatus: string | null = null;
  private dispositionStatus: BehaviorSubject<string | null> =
    new BehaviorSubject<string | null>(this._dispositionStatus);
  public dispositionStatus$: Observable<string | null> =
    this.dispositionStatus.asObservable();
  public statusSub: Subscription;

  constructor(
    private apollo: Apollo,
    public dispositionsService: DealDispositionsService,
    private dialog: MatDialog
  ) {}
  ngOnInit() {
    this.statusSub = this.dispositionsService.dispositionStatuses$.subscribe(
      (statuses) => {
        let status = statuses.filter((value) => value.statusSequenceDetails)[0];
        if (status.statusSequenceDetails) {
          let statusSequence =
            status.statusSequenceDetails[0].status_sequence_id;
          let index = this.findDispositionStatus(statusSequence);
          if (index !== null) {
            if (this.disposition.dispositionStatuses) {
              this._dispositionStatus =
                this.disposition.dispositionStatuses[index].status_id;
            } else {
              this._dispositionStatus = null;
            }
          } else {
            this._dispositionStatus = null;
          }
          this.dispositionStatus.next(this._dispositionStatus);
        }
      }
    );
  }
  ngOnDestroy() {
    this.statusSub?.unsubscribe();
  }
  deleteDisposition() {
    const ref: MatDialogRef<ConfirmationModalComponent> = this.dialog.open(
      ConfirmationModalComponent,
      {
        width: '380px',
        height: '200px',
        data: {
          modalTitle: 'Delete Disposition',
          actionMessage: 'Are you sure you want to delete this Disposition?',
          modalInformation: [],
        },
        backdropClass: 'confirmDialogComponent',
        hasBackdrop: true,
      }
    );
    ref.afterClosed().subscribe((data) => {
      if (data.clicked === 'Ok') {
        this.dispositionsService.deleteDisposition(this.disposition.id);
      }
    });
  }
  saveDisposition() {
    let {
      dispositionId,
      dispositionInput,
      dispositionDates,
      dispositionPipelineDates,
      dispositionStatusInput,
    } = this.prepareForSave();
    this.dispositionsService.updateDisposition(
      dispositionId,
      dispositionInput,
      dispositionDates,
      dispositionPipelineDates,
      dispositionStatusInput
    );
  }
  resetOrg($event: string | null) {
    this.disposition.purchaser = $event;
    this.disposition.organization = undefined;
    this.disposition.organization_id = null;
  }
  selectOrg(org: Organization) {
    this.disposition.organization = org;
    this.disposition.organization_id = org.id;
    this.disposition.purchaser = null;
    (document.activeElement as HTMLElement).blur();
  }
  findDispositionStatus(status_sequence_id: number): number | null {
    let dispositionStatus = null;
    if (this.disposition.dispositionStatuses) {
      // Doing a loose comparison because sometimes the status_sequence_id ends up as a string
      // and I can't be bothered to figure out why.
      dispositionStatus = this.disposition.dispositionStatuses.findIndex(
        (value) => value.status_sequence_id == status_sequence_id
      );
      if (dispositionStatus === -1) {
        dispositionStatus = null;
      }
    }
    return dispositionStatus;
  }
  changeDispositionStatus($event: Event) {
    let target = $event.target as HTMLSelectElement;
    let option = target.selectedOptions[0];
    let statusSequence =
      option?.attributes.getNamedItem('data-status-sequence')?.value ?? '';

    if (statusSequence.length && this.disposition.dispositionStatuses) {
      let index = this.findDispositionStatus(statusSequence as any);

      if (index !== null) {
        this.disposition.dispositionStatuses[index].status_id = option.value;
      } else {
        let dispositionStatus = {
          status_id: option.value,
          status_sequence_id: parseInt(statusSequence),
          disposition_id: this.disposition.id,
        };
        this.disposition.dispositionStatuses.push(dispositionStatus);
      }
      this._dispositionStatus = option.value;
      this.dispositionStatus.next(this._dispositionStatus);
    }
  }
  prepareForSave(): {
    dispositionId: string;
    dispositionInput: DispositionInput;
    dispositionDates: DispositionDate[];
    dispositionPipelineDates: DispositionPipelineDate[];
    dispositionStatusInput: UpsertDispositionStatus | null;
  } {
    let {
      board_consent_na,
      investor_consent_na,
      id,
      organization_id,
      purchaser,
      disposition_bov_id,
      disposition_legal_status_id,
      disposition_type_id,
      disposition_yield_request_status_id,
      related_party,
      crea_rofr,
      recapture_bond,
      recapture_exposure,
      dispositionDates,
      dispositionPipelineDates,
      total_distribution_amount,
      monitoring_compliance,
      dispositionStatuses,
      is_final,
    } = this.disposition;

    if (!dispositionDates) {
      dispositionDates = [];
    }
    if (!dispositionPipelineDates) {
      dispositionPipelineDates = [];
    }
    dispositionDates = dispositionDates.map((value) => {
      let { id, date, date_class_id, date_type_id, disposition_id } = value;
      return { id, date, date_class_id, date_type_id, disposition_id };
    });
    dispositionPipelineDates = dispositionPipelineDates.map((value) => {
      let { id, disposition_id, pipeline_milestone_id, date } = value;
      return { id, disposition_id, pipeline_milestone_id, date };
    });
    let [dispositionStatus] = dispositionStatuses ?? [];
    let dispositionStatusInput = null;
    if (
      dispositionStatus &&
      dispositionStatus.status_sequence_id &&
      dispositionStatus.disposition_id
    ) {
      dispositionStatusInput = ((): UpsertDispositionStatus => {
        let { id, status_id, status_sequence_id, disposition_id } =
          dispositionStatus;
        return { id, status_id, status_sequence_id, disposition_id };
      })();
    }
    return {
      dispositionId: id,
      dispositionInput: {
        board_consent_na,
        investor_consent_na,
        organization_id,
        purchaser,
        disposition_bov_id,
        disposition_legal_status_id,
        disposition_type_id,
        disposition_yield_request_status_id,
        related_party,
        crea_rofr,
        recapture_bond,
        recapture_exposure,
        total_distribution_amount,
        monitoring_compliance,
        is_final,
      },
      dispositionDates,
      dispositionPipelineDates,
      dispositionStatusInput,
    };
  }
  updateDate($event: DateOutput) {
    if (
      $event.pipeline_milestone_id &&
      this.disposition.dispositionPipelineDates
    ) {
      let index = this.disposition.dispositionPipelineDates.findIndex(
        (value) =>
          value.pipeline_milestone_id === $event.pipeline_milestone_id &&
          value.id === $event.id
      );
      if (index > -1) {
        this.disposition.dispositionPipelineDates[index].date = $event.date;
      } else {
        this.disposition.dispositionPipelineDates.push(
          $event as DispositionPipelineDate
        );
      }
    } else if ($event.date_type_id && this.disposition.dispositionDates) {
      let index = this.disposition.dispositionDates.findIndex(
        (value) =>
          value.date_type_id === $event.date_type_id && $event.id === value.id
      );
      if (index > -1) {
        this.disposition.dispositionDates[index].date = $event.date;
      } else {
        this.disposition.dispositionDates.push($event);
      }
    }
  }
}
